import {publicClient} from 'common/util/public-client';
import {FetchOptions} from 'common/util/shared-client';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {EventDefinition, PropertyDefinition} from 'common/report/queries/app-api/data-definitions-types';
import {Dashboard} from 'common/report/dashboards/types';
import {DashboardsModelClient} from 'common/report/controllers/types';

import {appApiUrl} from '../utils';

/* Public Dashboards are app_api/public/views.py. Should only be used within unauthenticated context (e.g. Dashboard App in Public Mode)*/
class PublicDashClient implements DashboardsModelClient {
  /** Sends a HTTP GET request to retrieve a specific dashboard */
  querySingle({dashboardID}: {dashboardID: number | string}, options: FetchOptions & {clearCache: boolean}) {
    if (options.clearCache && !Object.prototype.hasOwnProperty.call(options, `fromFetchCache`)) {
      options.fromFetchCache = false;
    }

    return publicClient.get<Dashboard>(
      appApiUrl(`public/dashboards/${dashboardID}/`),
      {},
      {
        throwOnInvalidStatus: true,
        ...options,
      },
    );
  }

  verifyPasscode(dashboardId: string, passcode: string) {
    return publicClient.post<Dashboard>(
      appApiUrl(`public/verify/${dashboardId}/`),
      {password: passcode},
      {throwOnInvalidStatus: true},
    );
  }

  queryCard({
    uuid,
    bookmarkId,
    cardId,
    endpoint,
    client_version: clientVersion,
    time_comparison: timeComparison,
    benchmarkCardId,
    query_origin: queryOrigin,
  }: {
    uuid: string;
    bookmarkId?: number;
    cardId: number;
    endpoint: string;
    client_version?: string;
    time_comparison?: string;
    benchmarkCardId?: number;
    query_origin?: string;
  }) {
    return publicClient.post(appApiUrl(`public/dashboard-cards`), {
      uuid,
      ...(bookmarkId ? {bookmark_id: bookmarkId} : {}),
      // bookmark_id: bookmarkId,
      card_id: cardId,
      // TODO: endpoint should be calculated server-side, can't trust user on either
      endpoint,
      // time comparison needed for metrics cards to compare diff
      ...(timeComparison ? {time_comparison: timeComparison} : {}),
      ...(clientVersion ? {client_version: clientVersion} : {}),
      // Benchmark cards do not have bookmark_id
      ...(benchmarkCardId ? {benchmark_card_id: benchmarkCardId} : {}),
      query_origin: queryOrigin,
    });
  }

  getMetadata(dashboardId: string, options: FetchOptions & {clearCache: boolean}) {
    if (options.clearCache && !Object.prototype.hasOwnProperty.call(options, `fromFetchCache`)) {
      options.fromFetchCache = false;
    }

    return publicClient.get<{
      is_free_plan: boolean;
      project_timezone: string;
      utc_offset: number;
      data_groups: Record<string, DataGroup>;
      event_definitions?: EventDefinition[];
      event_properties?: PropertyDefinition[];
      people_properties?: PropertyDefinition[];
      org_id: number;
      project_as_of_date: string | null;
    }>(
      appApiUrl(`public/dashboards/${dashboardId}/metadata`),
      {},
      {
        throwOnInvalidStatus: true,
        ...options,
      },
    );
  }
}

export type PublicDashboardsClient = InstanceType<typeof PublicDashClient>;
export const publicDashboardsClient = new PublicDashClient();
